import { Check, X, CreditCard, Clock, Package, Download, CheckCircle, Ban } from 'lucide-react';

/**
 * Visual horizontal stepper showing an order's progress through the lifecycle.
 * Steps: Paid -> Accepted -> Delivered -> Completed
 * Divergent states (denied, cancelled, refunded, cancelled_expired) render as a
 * single terminal red step so buyers/artists immediately know the flow ended.
 */

const STEPS = [
  { key: 'paid', label: 'Placed', icon: CreditCard },
  { key: 'accepted', label: 'Accepted', icon: Package },
  { key: 'delivered', label: 'Delivered', icon: Download },
  { key: 'completed', label: 'Completed', icon: CheckCircle },
];

// Which steps are "reached" (green) for each order status
const STATUS_PROGRESS = {
  pending_payment: -1,   // nothing reached yet
  paid: 0,
  accepted: 1,
  delivered: 2,
  completed: 3,
};

const TERMINAL_LABELS = {
  denied: { label: 'Denied by Artist', icon: X },
  cancelled: { label: 'Cancelled', icon: Ban },
  cancelled_expired: { label: 'Expired & Refunded', icon: Clock },
  refunded: { label: 'Refunded', icon: Ban },
};

export function OrderStatusTracker({ status }) {
  const isTerminal = status && Object.prototype.hasOwnProperty.call(TERMINAL_LABELS, status);

  if (isTerminal) {
    const { label, icon: Icon } = TERMINAL_LABELS[status];
    return (
      <div className="w-full mb-4" data-testid="order-status-tracker">
        <div className="flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
          <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 text-red-400" />
          </div>
          <span className="text-sm font-medium text-red-400">{label}</span>
        </div>
      </div>
    );
  }

  const reached = STATUS_PROGRESS[status] ?? -1;

  return (
    <div className="w-full mb-4" data-testid="order-status-tracker">
      <div className="flex items-start justify-between relative">
        {STEPS.map((step, idx) => {
          const isDone = idx <= reached;
          const isCurrent = idx === reached;
          const StepIcon = isDone ? Check : step.icon;

          const stepDotClasses = isDone
            ? 'bg-lime border-lime text-black'
            : isCurrent
              ? 'bg-lime/10 border-lime text-lime'
              : 'bg-[#0a0a0a] border-[#333] text-[#555]';

          const labelClasses = isDone || isCurrent ? 'text-white' : 'text-[#666]';

          return (
            <div key={step.key} className="flex-1 flex flex-col items-center relative min-w-0" data-testid={`tracker-step-${step.key}`}>
              {/* Line connecting to previous step */}
              {idx > 0 && (
                <div
                  className={`absolute top-4 right-1/2 w-full h-0.5 -z-0 ${
                    idx <= reached ? 'bg-lime' : 'bg-[#262626]'
                  }`}
                  aria-hidden="true"
                />
              )}
              <div className={`relative z-10 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${stepDotClasses} ${isCurrent ? 'animate-pulse' : ''}`}>
                <StepIcon className="w-4 h-4" />
              </div>
              <span className={`mt-1.5 text-[10px] sm:text-xs font-medium text-center px-1 truncate w-full ${labelClasses}`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
